import React, { useEffect, useState } from "react";

import { getDeals } from "api/requests/getDeals";
import { Action, Category } from "constants/events";
import { Deal } from "custom-types/Deals";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import { useEventTracker } from "hooks/useEventTracker";
import useGetDealKindLabel from "hooks/useGetDealKindLabel";
import { useDispatch, useSelector } from "redux/hooks";
import { getCoordinates } from "redux/selectors/location";
import { getUserId } from "redux/selectors/user";
import { dealCardUpdateMedRec } from "utils/dealCardUpdateMedRec";
import { getDispensaryUrl } from "utils/dispensaryUrls";
import { getRoundedDistance } from "utils/distance";

import Carousel from "components/botanic/Carousel";
import DealCard, { DealCardPlaceholder } from "components/botanic/DealCard";
import DealsPromosNoResults from "components/DealsPromosNoResults";

const DealsCarousel: React.FC<{
  className?: string;
  cardsPerSlide?: number;
  skip?: number;
  take?: number;
}> = ({ cardsPerSlide = 4, className, skip, take = 8 }) => {
  const countryCode = useDomainCountryCode();
  const userId = useSelector(getUserId);
  const coordinates = useSelector(getCoordinates);

  const dispatch = useDispatch();

  const { publishEvent } = useEventTracker();
  const { getDealKindLabel } = useGetDealKindLabel();

  const [deals, setDeals] = useState<Deal[]>();

  useEffect(() => {
    const fetchDeals = async () => {
      const { deals } = await getDeals(coordinates, countryCode, skip, take);

      setDeals(deals);
    };

    fetchDeals();
  }, [JSON.stringify(coordinates), countryCode]);

  const cardClick = async (deal: Deal) => {
    const {
      dispensary: { id: dispensaryId },
    } = deal;

    publishEvent({
      action: Action.click,
      category: Category.dealCards,
      dealId: deal.id,
      dispensaryId,
      label: getDealKindLabel(deal),
      userId,
    });

    // Med/rec deals will have medical and/or recreational properties, check if we need to update the user preference
    if (deal.isMedical || deal.isRecreational) {
      await dealCardUpdateMedRec(deal, dispatch);
    }
  };

  const cardImpression = (deal: Deal) => {
    const {
      dispensary: { id: dispensaryId, distanceMi },
    } = deal;

    publishEvent({
      action: Action.impression,
      category: Category.dealCards,
      dealId: deal.id,
      dispensaryDistance: getRoundedDistance(distanceMi, countryCode),
      dispensaryId,
      label: getDealKindLabel(deal),
      userId,
    });
  };

  return (
    <Carousel
      className={className}
      title="Deals on nearby weed"
      cardsPerSlide={cardsPerSlide}
      noResultsDisplay={<DealsPromosNoResults type="deals" />}
      seeMoreText="View all"
      seeMoreHref="/deals"
    >
      {deals === undefined ? (
        Array.from({ length: cardsPerSlide }).map((_, index) => (
          <DealCardPlaceholder
            className="rounded bg-white shadow-low h-full"
            key={`deal-card-placeholder-${index}`}
          />
        ))
      ) : deals.length ? (
        deals.map((deal) => {
          const {
            title,
            dispensary: { slug },
          } = deal;

          const isDealCBD = !deal.isMedical && !deal.isRecreational;
          const dealType =
            deal.isMedical && !deal.isRecreational ? "Med" : "Rec";

          const dealUrl = getDispensaryUrl({
            countryCode,
            query: {
              deal_title: [title],
              ...(!isDealCBD && { menu_type: dealType }),
            },
            slug,
            tab: "menu",
          });

          return (
            <DealCard
              as="a"
              className="rounded bg-white shadow-low h-full overflow-hidden"
              countryCode={countryCode}
              deal={{ ...deal, dealUrl }}
              key={deal.id}
              onClick={() => cardClick(deal)}
              onLoad={() => cardImpression(deal)}
              showFulfillment={true}
            />
          );
        })
      ) : (
        <></>
      )}
    </Carousel>
  );
};

export default DealsCarousel;
